/**
 * TripHeroSection — cover image + destination + dates.
 * First card at the top of Trip Details hub.
 */
import { MapPin, Calendar, Heart } from "lucide-react";
import { BACKEND_URL } from "@/config/api";

const flag = (cc) => cc ? cc.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0))) : "";

function formatRange(start, end) {
  if (!start) return "Dates TBC";
  const s = new Date(start);
  const e = end ? new Date(end) : null;
  const fmt = (d, o) => d.toLocaleDateString("en-GB", o);
  if (!e || s.toDateString() === e.toDateString()) return fmt(s, { day: "numeric", month: "long", year: "numeric" });
  if (s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth()) {
    return `${s.getDate()}–${e.getDate()} ${fmt(s, { month: "long", year: "numeric" })}`;
  }
  return `${fmt(s, { day: "numeric", month: "short" })} – ${fmt(e, { day: "numeric", month: "short", year: "numeric" })}`;
}

export default function TripHeroSection({ trip }) {
  const src = trip.cover_photo_id ? `${BACKEND_URL}/api/photos/${trip.cover_photo_id}/file` : null;
  return (
    <div className="relative h-52 rounded-2xl overflow-hidden bg-gradient-to-br from-sky-600 to-indigo-800" data-testid="trip-hero">
      {src && (
        <img src={src} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
      <div className="relative h-full flex flex-col justify-end p-5 text-white">
        <p className="text-xs uppercase tracking-widest opacity-80 flex items-center gap-1.5">
          {trip.is_joint ? <><Heart className="w-3 h-3 fill-pink-300 text-pink-300" /> Shared trip</> : "Trip"}
        </p>
        <h2 className="font-serif text-3xl leading-tight mt-1">{trip.title}</h2>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm opacity-95 mt-2">
          {(trip.location || trip.country) && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> {flag(trip.country_code)} {trip.location || trip.country}
            </span>
          )}
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" /> {formatRange(trip.start_date || trip.target_date, trip.end_date)}
          </span>
        </div>
      </div>
    </div>
  );
}
